/**
 * Water Store - Zustand med Cosmos DB-synk
 * 
 * Hanterar vattenförråd med behållare.
 * Synkroniserar med backend API och fungerar offline med localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth-store';

// ============================================================================
// Types
// ============================================================================

export interface WaterContainer {
  id: string;
  name: string;
  liters: number;
  filledDate: string;
  location: string;
}

export interface WaterStats {
  dailyNeed: number;
  totalLiters: number;
  daysSupply: number;
  targetLiters: number;
  progress: number;
  containersNeedingRefresh: string[];
}

interface WaterState {
  // Data
  householdSize: number;
  pets: number;
  targetDays: number;
  containers: WaterContainer[];
  
  // Beräknade stats (uppdateras automatiskt)
  stats: WaterStats;
  
  // Synk-status
  lastSyncAt: string | null;
  hasPendingChanges: boolean;
  isSyncing: boolean;
  syncError: string | null;
  
  // Actions - Settings
  setHouseholdSize: (size: number, familyId?: string) => void;
  setPets: (pets: number, familyId?: string) => void;
  setTargetDays: (days: number, familyId?: string) => void;
  
  // Actions - Containers
  addContainer: (container: Omit<WaterContainer, 'id'>, familyId?: string) => void;
  updateContainer: (id: string, updates: Partial<WaterContainer>, familyId?: string) => void;
  deleteContainer: (id: string, familyId?: string) => void;
  refreshContainer: (id: string, familyId?: string) => void;
  
  // Synk
  fetchFromServer: (familyId?: string) => Promise<void>;
  syncToServer: () => Promise<void>;
  
  // Internal
  _recalculateStats: () => void;
  _syncSettingsToServer: (familyIdOverride?: string) => Promise<void>;
  _addContainerToServer: (container: WaterContainer, familyIdOverride?: string) => Promise<void>;
  _updateContainerOnServer: (id: string, updates: Partial<WaterContainer>, familyIdOverride?: string) => Promise<void>;
  _deleteContainerOnServer: (id: string, familyIdOverride?: string) => Promise<void>;
  _refreshContainerOnServer: (id: string, familyIdOverride?: string) => Promise<void>;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// API Client med automatisk token refresh
import { apiGet, apiPut, apiPost, apiDelete } from '@/services/api-client';
import { logActivity } from '@/lib/activity-logger';

// Konstanter baserade på Hemberedskapsskolan
const LITERS_PER_PERSON_PER_DAY = 5;
const LITERS_PER_PET_PER_DAY = 1;

// ============================================================================
// Store
// ============================================================================

export const useWaterStore = create<WaterState>()(
  persist(
    (set, get) => ({
      // Default values
      householdSize: 2,
      pets: 0,
      targetDays: 7,
      containers: [],
      stats: {
        dailyNeed: 10,
        totalLiters: 0,
        daysSupply: 0,
        targetLiters: 70,
        progress: 0,
        containersNeedingRefresh: [],
      },
      lastSyncAt: null,
      hasPendingChanges: false,
      isSyncing: false,
      syncError: null,
      _hasHydrated: false,

      setHouseholdSize: (size: number, familyId?: string) => {
        set({ householdSize: size, hasPendingChanges: true });
        get()._recalculateStats();
        get()._syncSettingsToServer(familyId);
      },

      setPets: (pets: number, familyId?: string) => {
        set({ pets, hasPendingChanges: true });
        get()._recalculateStats();
        get()._syncSettingsToServer(familyId);
      },

      setTargetDays: (days: number, familyId?: string) => {
        set({ targetDays: days, hasPendingChanges: true });
        get()._recalculateStats();
        get()._syncSettingsToServer(familyId);
      },

      addContainer: (container: Omit<WaterContainer, 'id'>, familyId?: string) => {
        const newContainer: WaterContainer = {
          ...container,
          id: Date.now().toString(),
          filledDate: container.filledDate || new Date().toISOString().split('T')[0],
        };
        
        set((state) => ({
          containers: [...state.containers, newContainer],
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._addContainerToServer(newContainer, familyId);
      },

      updateContainer: (id: string, updates: Partial<WaterContainer>, familyId?: string) => {
        set((state) => ({
          containers: state.containers.map(c =>
            c.id === id ? { ...c, ...updates } : c
          ),
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._updateContainerOnServer(id, updates, familyId);
      },

      deleteContainer: (id: string, familyId?: string) => {
        set((state) => ({
          containers: state.containers.filter(c => c.id !== id),
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._deleteContainerOnServer(id, familyId);
      },

      refreshContainer: (id: string, familyId?: string) => {
        const today = new Date().toISOString().split('T')[0];
        set((state) => ({
          containers: state.containers.map(c =>
            c.id === id ? { ...c, filledDate: today } : c
          ),
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._refreshContainerOnServer(id, familyId);
      },

      _recalculateStats: () => {
        const { householdSize, pets, targetDays, containers } = get();
        
        const dailyNeed = (householdSize * LITERS_PER_PERSON_PER_DAY) + (pets * LITERS_PER_PET_PER_DAY);
        const totalLiters = containers.reduce((sum, c) => sum + c.liters, 0);
        const daysSupply = dailyNeed > 0 ? totalLiters / dailyNeed : 0;
        const targetLiters = dailyNeed * targetDays;
        const progress = targetLiters > 0 ? Math.round((totalLiters / targetLiters) * 100) : 0;

        // Kolla vilka behållare som behöver bytas (äldre än 6 månader)
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const containersNeedingRefresh = containers
          .filter(c => new Date(c.filledDate) < sixMonthsAgo)
          .map(c => c.id);

        set({
          stats: {
            dailyNeed,
            totalLiters,
            daysSupply,
            targetLiters,
            progress,
            containersNeedingRefresh,
          },
        });
      },

      // Server sync helpers
      _syncSettingsToServer: async (familyIdOverride?: string) => {
        const { householdSize, pets, targetDays } = get();
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = familyIdOverride || ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
        await apiPut(`/water${qs}`, { householdSize, pets, targetDays });
      },

      _addContainerToServer: async (container: WaterContainer, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId2 = getEffectiveFamilyOwnerId();
        const familyId2 = familyIdOverride || ownerId2 || useAuthStore.getState().user?.familyId;
        const qs2 = familyId2 ? `?familyId=${encodeURIComponent(familyId2)}` : '';
        const result = await apiPost<{ container: WaterContainer }>(`/water/containers${qs2}`, container);
        if (result.success && result.data?.container?.id) {
          logActivity({ action: 'ADD_WATER_CONTAINER', category: 'water', description: 'Water container added', metadata: { containerId: result.data.container.id, name: container.name } });
          set((state) => ({
            containers: state.containers.map(c =>
              c.id === container.id ? { ...c, id: result.data!.container.id } : c
            ),
          }));
        }
      },

      _updateContainerOnServer: async (id: string, updates: Partial<WaterContainer>, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId3 = getEffectiveFamilyOwnerId();
        const familyId3 = familyIdOverride || ownerId3 || useAuthStore.getState().user?.familyId;
        const qs3 = familyId3 ? `?familyId=${encodeURIComponent(familyId3)}` : '';
        const result = await apiPut(`/water/containers/${id}${qs3}`, updates);
        if (result.success) {
          logActivity({ action: 'UPDATE_WATER_CONTAINER', category: 'water', description: 'Water container updated', metadata: { containerId: id } });
        }
      },

      _deleteContainerOnServer: async (id: string, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId4 = getEffectiveFamilyOwnerId();
        const familyId4 = familyIdOverride || ownerId4 || useAuthStore.getState().user?.familyId;
        const qs4 = familyId4 ? `?familyId=${encodeURIComponent(familyId4)}` : '';
        const result = await apiDelete(`/water/containers/${id}${qs4}`);
        if (result.success) {
          logActivity({ action: 'DELETE_WATER_CONTAINER', category: 'water', description: 'Water container deleted', metadata: { containerId: id } });
        }
      },

      _refreshContainerOnServer: async (id: string, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId5 = getEffectiveFamilyOwnerId();
        const familyId5 = familyIdOverride || ownerId5 || useAuthStore.getState().user?.familyId;
        const qs5 = familyId5 ? `?familyId=${encodeURIComponent(familyId5)}` : '';
        const result = await apiPost(`/water/containers/${id}/refresh${qs5}`);
        if (result.success) {
          logActivity({ action: 'ROTATE_WATER', category: 'water', description: 'Water container refreshed', metadata: { containerId: id } });
        }
      },

      fetchFromServer: async (familyId?: string) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          // Inte inloggad - använd lokal data
          return;
        }

        set({ isSyncing: true, syncError: null });

        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId6 = getEffectiveFamilyOwnerId();
          const resolvedFamilyId = familyId || ownerId6 || useAuthStore.getState().user?.familyId;
          const qs6 = resolvedFamilyId ? `?familyId=${encodeURIComponent(resolvedFamilyId)}` : '';
          const result = await apiGet<{
            householdSize: number;
            pets: number;
            targetDays: number;
            containers: WaterContainer[];
          }>(`/water${qs6}`);
          
          if (result.success && result.data) {
            const { householdSize, pets, targetDays, containers } = result.data;
            
            set({
              householdSize,
              pets,
              targetDays,
              containers: containers || [],
              isSyncing: false,
              hasPendingChanges: false,
              lastSyncAt: new Date().toISOString(),
            });
            
            get()._recalculateStats();
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.warn('Failed to fetch water data:', error);
          set({
            isSyncing: false,
            syncError: 'Failed to fetch water data',
          });
        }
      },

      syncToServer: async () => {
        // Full sync implementeras vid behov
        // Just nu synkas ändringar direkt
        set({ hasPendingChanges: false, lastSyncAt: new Date().toISOString() });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
        // Räkna om stats efter hydration
        get()._recalculateStats();
      },
    }),
    {
      name: 'beredskapshubben-water',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        householdSize: state.householdSize,
        pets: state.pets,
        targetDays: state.targetDays,
        containers: state.containers,
        lastSyncAt: state.lastSyncAt,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// ============================================================================
// Hooks
// ============================================================================

export const useWaterHydrated = () => useWaterStore((state) => state._hasHydrated);
